import React, { useMemo, useState } from "react";
import { MdCheck, MdSearch } from "react-icons/md";

export type InlineMultiSelectOption = {
  value: number | string;
  label: string;
};

type InlineMultiSelectProps = {
  value: Array<number | string>;
  options: InlineMultiSelectOption[];
  onChange: (next: Array<number | string>) => void;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  isArabic?: boolean;
  className?: string;
  selectionSummary?: string;
  showSelectAll?: boolean;
};

/**
 * Inline multi-select for modals — no floating menu, so nothing overlaps footer buttons.
 */
export default function InlineMultiSelect({
  value,
  options,
  onChange,
  placeholder,
  loading = false,
  disabled = false,
  isArabic = false,
  className = "",
  selectionSummary,
  showSelectAll = false,
}: InlineMultiSelectProps) {
  const [query, setQuery] = useState("");
  const dir = isArabic ? "rtl" : "ltr";

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) => option.label.toLowerCase().includes(term));
  }, [options, query]);

  const selectedKeys = useMemo(() => new Set(value.map(String)), [value]);

  const visibleKeys = useMemo(() => filtered.map((option) => String(option.value)), [filtered]);
  const allVisibleSelected = visibleKeys.length > 0 && visibleKeys.every((key) => selectedKeys.has(key));

  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      onChange(value.filter((item) => !visibleKeys.includes(String(item))));
      return;
    }

    const merged = [...value];
    filtered.forEach((option) => {
      if (!selectedKeys.has(String(option.value))) merged.push(option.value);
    });
    onChange(merged);
  };

  const toggle = (nextValue: number | string) => {
    const key = String(nextValue);
    if (selectedKeys.has(key)) {
      onChange(value.filter((item) => String(item) !== key));
      return;
    }
    onChange([...value, nextValue]);
  };

  return (
    <div
      dir={dir}
      className={`overflow-hidden rounded-2xl border border-white/10 bg-[var(--apple-field)] ${
        disabled ? "opacity-60" : ""
      } ${className}`.trim()}
    >
      <label className="flex cursor-text items-center gap-2.5 border-b border-white/10 px-3 py-0">
        <MdSearch className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
        <input
          type="text"
          role="searchbox"
          inputMode="search"
          autoComplete="off"
          value={query}
          disabled={disabled || loading}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder ?? (isArabic ? "ابحث..." : "Search...")}
          className="min-w-0 flex-1 appearance-none border-0 bg-transparent py-3 text-start text-sm text-white outline-none placeholder:text-slate-500"
        />
        <span className="shrink-0 rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-slate-200">
          {value.length}
        </span>
      </label>

      {showSelectAll && filtered.length > 0 ? (
        <div className="border-b border-white/10 px-3 py-2">
          <button
            type="button"
            onClick={toggleAllVisible}
            disabled={disabled || loading}
            className="text-xs font-semibold text-brand-300 transition-colors hover:text-brand-200 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={allVisibleSelected
              ? isArabic ? "إلغاء اختيار الكل" : "Clear all"
              : isArabic ? "اختيار الكل" : "Select all"}
          >
            {allVisibleSelected
              ? isArabic ? "إلغاء اختيار الكل" : "Clear all"
              : isArabic ? "اختيار الكل" : "Select all"}
          </button>
        </div>
      ) : null}

      <div className="max-h-44 overflow-y-auto py-1">
        {loading ? (
          <p className="px-3 py-4 text-center text-xs text-slate-400">
            {isArabic ? "جارٍ التحميل..." : "Loading..."}
          </p>
        ) : filtered.length === 0 ? (
          <p className="px-3 py-4 text-center text-xs text-slate-400">
            {options.length === 0
              ? isArabic
                ? "لا توجد اختيارات"
                : "No options"
              : isArabic
                ? "لا توجد نتائج"
                : "No results"}
          </p>
        ) : (
          filtered.map((option) => {
            const selected = selectedKeys.has(String(option.value));
            return (
              <button
                key={String(option.value)}
                type="button"
                disabled={disabled}
                onClick={() => toggle(option.value)}
                aria-pressed={selected}
                className={`flex w-full items-center gap-3 px-3 py-2.5 text-start text-sm transition-colors hover:bg-white/5 ${
                  selected ? "bg-brand-500/10" : ""
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                    selected
                      ? "border-brand-500 bg-brand-500 text-white"
                      : "border-white/20 bg-transparent"
                  }`}
                >
                  {selected ? <MdCheck className="h-3.5 w-3.5" aria-hidden /> : null}
                </span>
                <span className="min-w-0 flex-1 truncate font-medium text-white">
                  {option.label}
                </span>
              </button>
            );
          })
        )}
      </div>

      {value.length > 0 ? (
        <div className="border-t border-white/10 px-3 py-2">
          <p className="text-start text-xs text-slate-400">
            {selectionSummary ?? (isArabic
              ? `تم اختيار ${value.length} صف`
              : `${value.length} grade(s) selected`)}
          </p>
        </div>
      ) : null}
    </div>
  );
}
